import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { formatDate } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { BillingsMatrixTable } from '@/components/sales-trackers/BillingsMatrix'
import { ReattributeLine } from '@/components/sales-trackers/ReattributeLine'
import { TargetVsActual } from '@/components/sales-trackers/TargetVsActual'
import { useBillingsMySheet, useBillingsRepDetail } from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'
import { NoBillingsYet } from '@/components/sales-trackers/no-billings-yet'
import { isNoBillingsYet } from '@/components/sales-trackers/billings-empty'
import type { BillingsRepDetail } from '@/types'
import type { BillingsQuery } from '@/lib/api'

/**
 * One rep's sheet: their totals, targets, products, monthly split and lines.
 *
 * Lifted out of RepDetail.tsx unchanged, so the markup that was already reviewed for
 * accessibility carries over rather than being rewritten. Two routes now render it:
 * the By Rep page with a dropdown, and /sales-trackers/reps/:ident for a deep link.
 *
 * IT DELIBERATELY RENDERS NO <h1>. Overview, Reps, Products and RepDetail all set
 * id="sales-trackers-heading" on their own heading, and the accessibility spec asserts
 * every page exposes exactly that landmark. A second heading with the same id inside a
 * composed page would be a duplicate DOM id and a broken landmark.
 *
 * It also owns no fiscal year and no rep selection: both are the page's business, so
 * the same component serves an admin who picked a rep from a list and, later, a rep
 * looking at themselves. When that happens this points at a session-scoped endpoint
 * and nothing below changes.
 *
 * That endpoint must be a NEW one. Every read here takes the rep from a parameter, so
 * granting reps access to the current API would let one read a colleague's commission
 * by editing the address bar.
 */
interface RepSheetProps {
  /** A user id (005…) or a bucket label. */
  ident?: string
  /**
   * The fiscal year AND the window inside it. A plain year was enough while every page
   * showed twelve months; it is not once a caller can ask for September, because the
   * sheet has to request the same window its page is displaying or the two disagree.
   */
  query?: BillingsQuery
  /** Omit to render read-only, which is what a rep viewing themselves will get. */
  onEditTargets?: () => void
  /**
   * The individual billing lines and the Reattribute action.
   *
   * Off for a rep looking at their own sheet, per Jason's second review. NOT removed
   * outright: the Reattribute button inside this card is the ONLY trigger for the
   * reattribution dialog in the whole frontend, and reattribution is the main thing
   * anyone does with this data (1,953 of 1,985 workbook corrections). Deleting the card
   * would leave PUT /attribution/overrides reachable only by curl.
   */
  showLines?: boolean
  /**
   * Render the SIGNED-IN rep's own sheet from /billings/me/sheet instead of looking one
   * up by id. `ident` is ignored entirely in this mode, which is the point: a rep's page
   * must not have an identifier in it for anybody to change.
   */
  selfService?: boolean
}

export function RepSheet({
  ident,
  query = {},
  onEditTargets,
  showLines = true,
  selfService = false,
}: RepSheetProps) {
  const monthly = useOverflowFocus('Monthly totals split by software and service')
  const lines = useOverflowFocus('Individual billing lines')
  const customers = useOverflowFocus('Top customers by revenue')
  // The line being reattributed. This replaces the spreadsheet's Corrected Sales Rep
  // column and is the main action here: measured on the real workbook, 1,953 of 1,985
  // corrections were re-attributions of lines that resolved fine.
  const [reattributing, setReattributing] = useState<BillingsRepDetail['lines'][number] | null>(
    null
  )

  // Both are declared unconditionally, as hooks must be, and one is disabled. The admin
  // lookup never fires in self-service mode, so a rep's page makes no request that
  // carries an identifier.
  const adminView = useBillingsRepDetail(selfService ? undefined : ident, query)
  const ownView = useBillingsMySheet(query, selfService)
  const rep = selfService ? ownView : adminView
  const money = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 0 })
  const data = rep.data
  // Named from the response rather than a prop: the sheet renders whatever window came
  // back, so reading the label off the same payload is what keeps the two in step.
  const periodName =
    data == null
      ? 'this period'
      : data.months.length === 1
        ? (data.months[0]?.label ?? 'this month')
        : `FY${data.fiscal_year}`

  if (!selfService && !ident) {
    return (
      <p className="text-sm text-muted-foreground">
        Choose a rep to see their sales, targets and lines.
      </p>
    )
  }

  return (
    <>
      {rep.isError &&
        (isNoBillingsYet(rep.error) ? (
          <NoBillingsYet what="Each rep's sales" />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not load these billings</AlertTitle>
            <AlertDescription>
              {explainError(rep.error, "Something went wrong loading this rep's sales.")}
            </AlertDescription>
          </Alert>
        ))}

      {rep.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">{periodName} total</p>
                <p
                  className="text-2xl font-semibold mt-1"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {money(data.fy_total)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Of which house accounts</p>
                <p
                  className="text-2xl font-semibold mt-1"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {money(data.house_total)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Billing lines</p>
                <p
                  className="text-2xl font-semibold mt-1"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {data.line_count.toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Jason's ask, in his words: "their targets, actual vs. target, and the
              percentage above or below". Same component as the company card, so a rep
              and the company are measured and presented identically. */}
          <TargetVsActual
            attainment={data.targets}
            monthCount={data.months.length}
            onEdit={onEditTargets}
            title="Their targets"
            periodLabel={periodName}
            isSingleMonth={data.months.length === 1}
          />

          {/* "Sales by product" for this rep. Same product_group grouping as the
              company-wide By Product page, so the two cannot disagree about what a
              product group is, and the same table component, so the header
              association and negative-number handling are not reimplemented. */}
          <Card>
            <CardHeader>
              <CardTitle>Sales by product</CardTitle>
            </CardHeader>
            <CardContent>
              <BillingsMatrixTable
                matrix={data.by_product}
                dimensionLabel="Product"
                caption={`Product breakdown by month for FY${data.fiscal_year}`}
                emptyMessage="No billings for this rep in this year."
              />
            </CardContent>
          </Card>

          {/* Jason's second review asked for this in place of the billing lines: the
              customers behind a rep's number, rather than every transaction. Same
              grouping as the company-wide Top clients card so the two agree. */}
          <Card>
            <CardHeader>
              <CardTitle>Top 10 customers by revenue</CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_customers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No billings for this rep in this year.
                </p>
              ) : (
                <div className="overflow-x-auto" ref={customers.ref} {...customers.regionProps}>
                  <table className="w-full text-sm">
                    <caption className="sr-only">
                      This rep&rsquo;s ten largest customers by revenue for FY
                      {data.fiscal_year}
                    </caption>
                    <thead>
                      <tr className="border-b">
                        <th scope="col" className="text-left font-medium py-2 pr-3 w-8">
                          #
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-4">
                          Customer
                        </th>
                        <th scope="col" className="text-right font-medium py-2 pr-4">
                          Billings
                        </th>
                        <th scope="col" className="text-right font-medium py-2">
                          Lines
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_customers.map((c) => (
                        <tr key={c.customer_name} className="border-b last:border-0">
                          <td className="py-1.5 pr-3 text-muted-foreground tabular-nums">
                            {c.rank}
                          </td>
                          <th scope="row" className="text-left font-normal py-1.5 pr-4">
                            {c.customer_name}
                          </th>
                          {/* Negatives carry a minus sign, never colour alone: a
                              credit-heavy customer is exactly the row a colour-blind
                              reader must not misread. */}
                          <td
                            className={`py-1.5 pr-4 text-right tabular-nums ${
                              c.amount < 0 ? 'text-destructive' : ''
                            }`}
                          >
                            {money(c.amount)}
                          </td>
                          <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                            {c.line_count.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto" ref={monthly.ref} {...monthly.regionProps}>
                <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <caption className="sr-only">
                    Monthly totals split by software and service for FY{data.fiscal_year}
                  </caption>
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Month
                      </th>
                      <th scope="col" className="text-right font-medium py-2 px-3">
                        Total
                      </th>
                      <th scope="col" className="text-right font-medium py-2 px-3">
                        Software
                      </th>
                      <th scope="col" className="text-right font-medium py-2 px-3">
                        Service
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.matrix.map((m) => (
                      <tr key={m.month} className="border-b last:border-0">
                        <th scope="row" className="text-left font-normal py-1.5 pr-4">
                          {m.label}
                        </th>
                        <td className="text-right py-1.5 px-3">{money(m.total)}</td>
                        <td className="text-right py-1.5 px-3">{money(m.software)}</td>
                        <td className="text-right py-1.5 px-3">{money(m.service)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2">
                      <th scope="row" className="text-left py-2 pr-4 font-semibold">
                        {data.months.length === 1 ? 'Total' : `${periodName} total`}
                      </th>
                      {/*
                        All three cells are summed from the SAME matrix.
                        
                        The first used to read data.fy_total, a figure computed over the
                        whole year, while the two beside it were summed from the rows on
                        screen. Identical while the sheet always showed twelve months;
                        under any narrower window the Total column stopped equalling
                        Software plus Service in its own row.
                      */}
                      <td className="text-right py-2 px-3 font-semibold">
                        {money(data.matrix.reduce((a, m) => a + m.total, 0))}
                      </td>
                      <td className="text-right py-2 px-3 font-semibold">
                        {money(data.matrix.reduce((a, m) => a + m.software, 0))}
                      </td>
                      <td className="text-right py-2 px-3 font-semibold">
                        {money(data.matrix.reduce((a, m) => a + m.service, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {showLines && (
            <Card>
              <CardHeader>
                <CardTitle>Billing lines</CardTitle>
              </CardHeader>
              <CardContent>
                {data.lines_truncated && (
                  <Alert role="note" className="mb-4">
                    <AlertTitle>Showing the most recent lines only</AlertTitle>
                    <AlertDescription>
                      {/*
                        This used to say "Export the CSV for the full set", which was not
                        true: that export covers only the lines already loaded, so past the
                        cap it silently produced the same partial 500. Transactions pages
                        the whole period and exports all of it.
                      */}
                      There are more lines than shown here. The{' '}
                      <Link className="underline" to="/sales-trackers/transactions">
                        Transactions page
                      </Link>{' '}
                      lists and exports the whole period.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="overflow-x-auto" ref={lines.ref} {...lines.regionProps}>
                  <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <caption className="sr-only">
                      Individual billing lines for FY{data.fiscal_year}
                    </caption>
                    <thead>
                      <tr className="border-b">
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Date
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Customer
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Product / service
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Invoice
                        </th>
                        <th scope="col" className="text-right font-medium py-2 px-3">
                          Amount
                        </th>
                        <th scope="col" className="text-right font-medium py-2 pl-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lines.map((l, i) => (
                        <tr key={`${l.doc_number}-${i}`} className="border-b last:border-0">
                          <td className="py-1.5 pr-3 whitespace-nowrap">
                            {formatDate(l.txn_date)}
                          </td>
                          <th scope="row" className="text-left font-normal py-1.5 pr-3">
                            {l.customer_name ?? '—'}
                            {/* House stated in words, not by colour alone. The space is
                              a real text node: a margin separates it visually but the
                              accessible name still read "Managershouse". */}
                            {l.is_house && (
                              <span className="ml-1.5 text-xs text-muted-foreground"> (house)</span>
                            )}
                          </th>
                          <td className="py-1.5 pr-3">{l.product_service ?? '—'}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{l.doc_number ?? '—'}</td>
                          <td
                            className={`text-right py-1.5 px-3 ${l.amount < 0 ? 'text-destructive' : ''}`}
                          >
                            {money(l.amount)}
                          </td>
                          <td className="text-right py-1.5 pl-3 whitespace-nowrap">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setReattributing(l)}
                              aria-label={`Reattribute the ${formatDate(l.txn_date)} line for ${l.customer_name ?? 'this customer'}`}
                            >
                              Reattribute
                            </Button>
                            {/* In text: a line already reattributed reads differently
                              from one straight out of QuickBooks. */}
                            {l.rep_source === 'override' && (
                              <span className="ml-1 text-xs text-muted-foreground">
                                set manually
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Mounted only alongside the lines card, which holds its only trigger. */}
      {showLines && (
        <ReattributeLine
          open={!!reattributing}
          onOpenChange={(o) => !o && setReattributing(null)}
          line={reattributing}
        />
      )}
    </>
  )
}
